'use client';

import React, { useState, useMemo } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { TypeERCPageData } from '@/app/[locale]/(shop)/account/early-reader-club/fetchPageData';

import LayoutProcessingErcSubscription from '@/components/account/ErcSchedule/layouts/LayoutProcessingErcSubscription';
import { HeaderSize, HeaderText } from '@/components/ui/HeaderText';

import { usePageData } from '@/context/PageDataProvider';
import { usePageTranslations } from '@/context/PageTranslationsProvider';

import { FeatureFlag } from '@/core/featureFlags';
import { TypeErcScheduleFields } from '@/core/generated-types';

import {
  isSubEngineSubscriptionCancelled,
  isSubEngineSubscriptionPaused,
  isSubEngineSubscriptionProcessing,
} from '@/lib/api/subscriptionManager/helpers/subscriptionHelpers';

import { SubEngineSubscriptionStatusEnum } from '@/lib/api/subscriptionManager/types';

import { CTA_NAMES, PAGE_LOCATIONS } from '@/utils/rudderstack';

import interpolate from '@/utils/translations/interpolate';
import { TypeAccountERCTranslations } from '@/utils/translations/types/TypeAccountERCTranslations';

import { Divider } from '../_components/Divider';
import { ItemSelector, TypeItem } from '../_components/ItemSelector';

import useDefaultSelectedSubscriptionId from './hooks/useDefaultSelectedSubscriptionId';

import { LayoutActiveErcSubscription, LayoutNoSubscription } from './layouts';
import LayoutCancelledSubscription from './layouts/LayoutCancelledSubscription';
import LayoutPausedErcSubscription from './layouts/LayoutPausedErcSubscription';

type ERCBadgeItem = TypeItem['badgeDetails'] & {
  text: keyof TypeAccountERCTranslations;
};

export const ERCBadgeProps: Record<string, ERCBadgeItem> = {
  [SubEngineSubscriptionStatusEnum.PAUSED]: {
    color: 'Purple',
    text: 'subscriptionStatusPaused',
  },
  [SubEngineSubscriptionStatusEnum.CANCELLED]: {
    color: 'GreyMidtone',
    text: 'subscriptionStatusCancelled',
  },
};

function ErcSchedule({
  subscriptionsIcons,
  noSubscriptionCtaLink,
}: TypeErcScheduleFields) {
  const { [FeatureFlag.SUB_SQUAD_ENABLE_ERC_PAUSE_STATE]: cancelSaverEnabled } =
    useFlags();

  const t = usePageTranslations() as TypeAccountERCTranslations;

  const data = usePageData() as TypeERCPageData;

  const { subscriptions } = data || {};
  const defaultSelectedSubscriptionId =
    useDefaultSelectedSubscriptionId(subscriptions);

  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>(
    defaultSelectedSubscriptionId,
  );

  const selectedSubscription = useMemo(
    () =>
      subscriptions.find(
        (subscription) => subscription.id === selectedSubscriptionId,
      ),
    [subscriptions, selectedSubscriptionId],
  );

  const isCancelled =
    selectedSubscription &&
    isSubEngineSubscriptionCancelled(selectedSubscription);

  const isPaused =
    selectedSubscription && isSubEngineSubscriptionPaused(selectedSubscription);

  const isProcessing =
    selectedSubscription &&
    isSubEngineSubscriptionProcessing(selectedSubscription);

  let content;

  if (!selectedSubscription) {
    content = (
      <LayoutNoSubscription
        t={t}
        noSubscriptionCtaLink={noSubscriptionCtaLink}
      />
    );
  } else if (isCancelled) {
    content = (
      <LayoutCancelledSubscription
        t={t}
        subscriptionId={selectedSubscription.id}
      />
    );
  } else if (isPaused) {
    content = (
      <LayoutPausedErcSubscription
        t={t}
        selectedSubscription={selectedSubscription}
        contentfulProductsObject={data.contentfulProductsObject}
      />
    );
  } else if (isProcessing) {
    content = <LayoutProcessingErcSubscription t={t} />;
  } else {
    content = (
      <LayoutActiveErcSubscription
        t={t}
        selectedSubscription={selectedSubscription}
        contentfulProductsObject={data.contentfulProductsObject}
      />
    );
  }

  const items: TypeItem[] = useMemo(
    () =>
      subscriptions.map((item, index) => {
        const baseProps = {
          id: item.id,
          title: `${t.itemLabel} ${index + 1}`,
          icon: subscriptionsIcons[index % subscriptionsIcons.length],
          subscriptionStatus: item.status,
        };

        const badgeProps = ERCBadgeProps[item.status];
        const shouldShowBadge = cancelSaverEnabled && badgeProps;

        return {
          ...baseProps,
          ...(shouldShowBadge && {
            badgeDetails: {
              color: badgeProps.color,
              text: t[badgeProps.text],
            },
          }),
        };
      }),
    [subscriptions, subscriptionsIcons, t, cancelSaverEnabled],
  );

  const selectedItem = useMemo(
    () => items.find(({ id }) => id === selectedSubscriptionId) || items[0],
    [items, selectedSubscriptionId],
  );

  return (
    <div data-testid="earlyReaderClubSchedule" className="mb-20">
      <ItemSelector
        items={items}
        selectedItemId={selectedSubscriptionId}
        setSelectedItemId={setSelectedSubscriptionId}
        trackCtaName={CTA_NAMES.CHILD_SELECTED}
        trackPageLocation={PAGE_LOCATIONS.EARLY_READER_CLUB_PAGE}
      />

      <HeaderText
        classes="text-h-x2 md:text-h-x1 mb-10"
        omitSizeString
        align="center"
        size={HeaderSize.H1}
        text={interpolate(t.activeSubscriptionsTitle, {
          nameLabel: selectedItem.title,
        })}
      />

      <Divider />

      {content}
    </div>
  );
}

export default ErcSchedule;
